import { useState } from "react";
import { cn } from "../../lib/cn";
import { initials } from "../../lib/format";
import { useGravatarUrl } from "../../lib/gravatar";

/**
 * App avatar. Resolution order: an explicit image (`src`, e.g. the better-auth
 * profile picture) → the Gravatar for `email` → initials. Gravatar is asked
 * with `d=404`, so "no Gravatar" surfaces as an image error and we fall back
 * to initials instead of a generic placeholder face.
 */
export function Avatar({
  name,
  email,
  src,
  size = 28,
  className,
}: {
  name: string;
  email?: string | null;
  src?: string | null;
  size?: number;
  className?: string;
}) {
  const gravatar = useGravatarUrl(email, size);
  const url = src || gravatar;
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  if (url && url !== failedUrl) {
    return (
      <img
        src={url}
        alt={name}
        title={name}
        width={size}
        height={size}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailedUrl(url)}
        className={cn(
          "inline-block shrink-0 rounded-full border border-line bg-surface-3 object-cover",
          className,
        )}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <div
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full border border-line bg-surface-3 font-semibold text-ink-muted",
        className,
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
      title={name}
    >
      {initials(name)}
    </div>
  );
}
